import { Component } from '@theme/component';

/** How long the button reads "Copied" before it goes back to "Copy", in milliseconds. */
const CONFIRMATION_DURATION = 1600;

/**
 * Copies an email address to the clipboard and briefly confirms it.
 *
 * Horizon's copy-to-clipboard component is not reused because it never resets its success message
 * and doesn't wait for, or handle a failure of, the clipboard write.
 *
 * Markup: blocks/pawwji-copy-email.liquid. The button calls `copy` through `on:click="/copy"`.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} address - The mailto link showing the address.
 * @property {HTMLElement} copiedLabel - The "Copied" label inside the button, reused for the announcement.
 * @property {HTMLElement} status - Visually hidden live region that announces a successful copy.
 *
 * @extends {Component<Refs>}
 */
class PawwjiCopyEmailComponent extends Component {
  requiredRefs = ['address', 'copiedLabel', 'status'];

  /** @type {number | undefined} */
  #resetTimer;

  /** @type {number | undefined} */
  #announceFrame;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
  }

  /**
   * Copies the address, then shows "Copied" for a moment. When the clipboard can't be written
   * (no permission, insecure context, older browser), it selects the address so it can be copied by hand.
   */
  async copy() {
    const email = this.dataset.email;
    if (!email) return;

    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(email);
    } catch {
      this.#selectAddress();
      return;
    }

    this.#confirm();
  }

  #confirm() {
    this.#reset();
    this.dataset.copied = '';

    // Clear and refill the live region on the next frame so a repeated copy is announced again.
    const { status, copiedLabel } = this.refs;
    this.#announceFrame = requestAnimationFrame(() => {
      status.textContent = copiedLabel.textContent?.trim() ?? '';
    });

    this.#resetTimer = window.setTimeout(() => this.#reset(), CONFIRMATION_DURATION);
  }

  #reset() {
    window.clearTimeout(this.#resetTimer);
    if (this.#announceFrame !== undefined) cancelAnimationFrame(this.#announceFrame);
    this.#resetTimer = undefined;
    this.#announceFrame = undefined;

    delete this.dataset.copied;
    const { status } = this.refs;
    if (status) status.textContent = '';
  }

  #selectAddress() {
    const selection = window.getSelection();
    const { address } = this.refs;
    if (!selection || !address) return;

    const range = document.createRange();
    range.selectNodeContents(address);
    selection.removeAllRanges();
    selection.addRange(range);
  }
}

if (!customElements.get('pawwji-copy-email-component')) {
  customElements.define('pawwji-copy-email-component', PawwjiCopyEmailComponent);
}
